import { FC, useState } from "react";

import { Box, Stack, useTheme } from "@mui/material";
import { RouteBuilders } from "@simplyk/common";

import { useLocaleContext } from "../../../../common/contexts/LocaleContext";
import { useMediaQuery } from "../../../../common/hooks/useMediaQuery";

import { useTranslate } from "@/common/hooks/useTranslate";
// eslint-disable-next-line zeffy-linter-frontend/no-cross-layer-imports -- legacy cross-layer import (pre-rule); migrate when touched
import { OwnerBannerLayout } from "@/components/NotificationBanners/OwnerBanner/OwnerBannerLayout";
// eslint-disable-next-line zeffy-linter-frontend/no-cross-layer-imports -- the owner banner and the dashboard org page editor share the same dialog
import { ShareOrgPageModal } from "@/components/ShareOrgPageModal/ShareOrgPageModal";
// eslint-disable-next-line zeffy-linter-frontend/no-cross-layer-imports -- legacy cross-layer import (pre-rule); migrate when touched
import { DashboardBadge } from "@/dashboard-design-system/Badge";
// eslint-disable-next-line zeffy-linter-frontend/no-cross-layer-imports -- legacy cross-layer import (pre-rule); migrate when touched
import { DashboardButton } from "@/dashboard-design-system/DashboardButton";
// eslint-disable-next-line zeffy-linter-frontend/no-cross-layer-imports -- legacy cross-layer import (pre-rule); migrate when touched
import { DashboardChip } from "@/dashboard-design-system/DashboardChip/DashboardChip";
import { Reveal, Hide } from "@/icons/outlined";

export type OrganizationPageOwnerBannerProps = {
  isPublished: boolean;
  onPublish: () => void;
  onUnpublish: () => void;
  isPublishLoading?: boolean;
  backToFormTableUrl: string;
};

export const OrganizationPageOwnerBanner: FC<OrganizationPageOwnerBannerProps> = ({
  isPublished,
  onPublish,
  onUnpublish,
  isPublishLoading,
  backToFormTableUrl,
}) => {
  const theme = useTheme();
  const { t } = useTranslate();
  const { isPhoneScreen } = useMediaQuery();
  const { isoLocale } = useLocaleContext();
  const buttonSize = isPhoneScreen ? "small" : "medium";
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);

  const editPageHref = RouteBuilders.buildOrganizationPageEditLink({ isoLocale }).path;
  const orgPageUrl = typeof window !== "undefined" ? window.location.origin + window.location.pathname : "";

  return (
    <>
      <OwnerBannerLayout
        slots={{
          info: (
            <Stack direction="row" alignItems="center" gap={1.25}>
              <DashboardChip
                label={
                  isPublished
                    ? t("organization", "ownerBanner.info.published")
                    : t("organization", "ownerBanner.info.notPublished")
                }
                variant="filled"
                vibe={isPublished ? "positive" : "neutral"}
                shape="rounded"
              />
              <Box display="flex" alignItems="center">
                <DashboardBadge
                  variant="dot"
                  sx={{
                    "& .MuiBadge-dot": {
                      backgroundColor: isPublished
                        ? theme.palette.text.positive.quiet
                        : theme.palette.text.neutral.supershy,
                    },
                  }}
                />
              </Box>
            </Stack>
          ),
          callout: isPublished ? (
            <DashboardButton
              startIcon={<Hide />}
              variant="outlined"
              vibe="danger"
              onClick={onUnpublish}
              sx={{ margin: { xs: theme.spacing(1, 0), md: undefined } }}
            >
              {t("organization", "ownerBanner.callout.unpublish")}
            </DashboardButton>
          ) : (
            <DashboardButton
              startIcon={<Reveal />}
              variant="outlined"
              vibe="positive"
              onClick={onPublish}
              isLoading={isPublishLoading}
              sx={{ margin: isPhoneScreen ? theme.spacing(1, 0) : undefined }}
            >
              {t("organization", "ownerBanner.callout.publish")}
            </DashboardButton>
          ),
          actions: (
            <Stack direction="row" gap={1}>
              <DashboardButton variant="outlined" size={buttonSize} href={editPageHref}>
                {t("organization", "ownerBanner.editPage")}
              </DashboardButton>
              {isPublished ? (
                <DashboardButton variant="filled" size={buttonSize} onClick={() => setIsShareModalOpen(true)}>
                  {t("organization", "ownerBanner.sharePageButton")}
                </DashboardButton>
              ) : (
                <DashboardButton variant="filled" href={backToFormTableUrl} size={buttonSize}>
                  {t("common", "backToDashboard")}
                </DashboardButton>
              )}
            </Stack>
          ),
        }}
      />
      <ShareOrgPageModal open={isShareModalOpen} onClose={() => setIsShareModalOpen(false)} orgPageUrl={orgPageUrl} />
    </>
  );
};
